import { CSSResult, html, LitElement, PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { Messages } from '../../../@types/wishlist.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { cleanupApiInstance, CleanupData, CleanupMode } from '../../../scripts/service/cleanup-api.js';
import baseLayerStyles from '../base-layer/base-layer.scss?inline';
import '../base-layer/base-layer.js';

@customElement('ents-wishlist-cleanup-layer')
export class WishlistCleanupLayer extends LitElement {
  @property({ type: Boolean }) showLayer: boolean = false;

  @consume({ context: i18nContext })
  private messages!: Messages;

  @state() private purchasedSelection: CleanupMode = 'NONE';
  @state() private notInAssortmentSelection: CleanupMode = 'NONE';

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(baseLayerStyles)];
  }

  protected updated(changedProperties: PropertyValues) {
    if (changedProperties.has('showLayer') && this.showLayer) {
      void this.loadPersistedState();
    }
  }

  private async loadPersistedState() {
    try {
      const cleanupSate = await cleanupApiInstance.getState();
      this.purchasedSelection = cleanupSate.purchased;
      this.notInAssortmentSelection = cleanupSate.notInAssortment;
    } catch {
      // TODO(ENTS-10834): show error state when the persisted state cannot be loaded
    }
  }

  private handleLayerClosed = () => {
    if (this.showLayer) {
      this.showLayer = false;
      this.dispatchEvent(new Event('cleanup-layer-closed', { composed: true, bubbles: true }));
    }
  };

  private async handleConfirm() {
    const data: CleanupData = {
      purchased: this.purchasedSelection,
      notInAssortment: this.notInAssortmentSelection,
    };

    const success = await cleanupApiInstance.cleanup(data);
    if (success) {
      this.handleLayerClosed();
    }
    // TODO(ENTS-10834): show error state when success is false
  }

  protected render() {
    // TODO(ENTS-10834): final UX pending UXD
    return html`
      <ents-base-layer
        drawer-test-id="ents-wishlist-cleanup-layer"
        ?showLayer="${this.showLayer}"
        @ents-layer-closed="${this.handleLayerClosed}"
      >
        <h3 class="ents-heading-medium" slot="title">Wunschliste aufräumen</h3>
        <div class="ents-wishlist-cleanup">
          <div class="ents-wishlist-cleanup__category">
            <label class="ents-copy-bold">
              Gekaufte Artikel
              <select
                class="ents-wishlist-cleanup__select"
                @change="${(e: Event) =>
                  (this.purchasedSelection = (e.target as HTMLSelectElement).value as CleanupMode)}"
              >
                <option value="NONE" ?selected="${this.purchasedSelection === 'NONE'}">Nicht entfernen</option>
                <option value="ONCE" ?selected="${this.purchasedSelection === 'ONCE'}">Einmalig entfernen</option>
                <option value="RECURRING" ?selected="${this.purchasedSelection === 'RECURRING'}">
                  Automatisch entfernen
                </option>
              </select>
            </label>
          </div>

          <div class="ents-wishlist-cleanup__category">
            <label class="ents-copy-bold">
              Nicht mehr verfügbare Artikel
              <select
                class="ents-wishlist-cleanup__select"
                @change="${(e: Event) =>
                  (this.notInAssortmentSelection = (e.target as HTMLSelectElement).value as CleanupMode)}"
              >
                <option value="NONE" ?selected="${this.notInAssortmentSelection === 'NONE'}">Nicht entfernen</option>
                <option value="ONCE" ?selected="${this.notInAssortmentSelection === 'ONCE'}">Einmalig entfernen</option>
                <option value="RECURRING" ?selected="${this.notInAssortmentSelection === 'RECURRING'}">
                  Automatisch entfernen
                </option>
              </select>
            </label>
          </div>
        </div>
        <div slot="footer" class="ents-wishlist-cleanup__footer">
          <button
            class="bdk-button"
            type="button"
            variant="primary"
            size="medium"
            @click="${this.handleConfirm}"
            data-test-ents-wishlist-cleanup-confirm
          >
            Bestätigen
          </button>
        </div>
      </ents-base-layer>
    `;
  }
}
